import * as React from "react";
import { ChevronDown } from "lucide-react";

import {
  FieldSelectFit,
  TRADE_LOG_COLUMNS_MENU_CHECKBOX_ITEM_CLASS,
  TRADE_LOG_COLUMNS_SELECT_TRIGGER_CLASS,
  TRADE_LOG_FILTER_INPUT_CLASS,
  TRADE_LOG_FILTER_SELECT_CONTENT_CLASS,
  TRADE_LOG_FILTER_SELECT_ITEM_CLASS,
  type TradeFilterSelectOption,
} from "@/components/trade-filter-selects";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { useIsBelowLg } from "@/hooks/use-mobile";

import type { WorkspaceNode, WorkspaceNodeStatus } from "../../../types/workspace";
import { WorkspaceDrawerCloseButton } from "./WorkspaceDrawerActionRow";
import {
  clearWorkspaceFocusFilter,
  collectWorkspaceFocusFilterOptions,
  isWorkspaceFocusFilterActive,
  toggleWorkspaceFocusGroup,
  WORKSPACE_FOCUS_TYPE_GROUPS,
  type WorkspaceFocusEnrichment,
  type WorkspaceFocusFilter,
  type WorkspaceFocusTypeGroupId,
  type WorkspaceFocusVisibilityMode,
  type WorkspaceSimulationFocusKind,
} from "./workspaceFocusFilter";

export type WorkspaceFocusFilterBarProps = {
  filter: WorkspaceFocusFilter;
  onChange: (next: WorkspaceFocusFilter) => void;
  nodes: WorkspaceNode[];
  enrichment: WorkspaceFocusEnrichment;
  className?: string;
};

const ALL_VALUE = "all";

const STATUS_LABELS: Partial<Record<WorkspaceNodeStatus, string>> = {
  draft: "Draft",
  processing: "Processing",
  complete: "Complete",
  failed: "Failed",
  archived: "Archived",
};

function toFilterValue(value: string): string {
  return value.trim() ? value : ALL_VALUE;
}

function fromFilterValue(value: string): string {
  return value === ALL_VALUE ? "" : value;
}

function focusTypeTriggerLabel(filter: WorkspaceFocusFilter): string {
  const selected = WORKSPACE_FOCUS_TYPE_GROUPS.filter((group) =>
    filter.groupIds.includes(group.id)
  );
  if (selected.length === 0) return "All types";
  const mode = (filter.visibilityMode ?? "dim") === "hide" ? "Hide" : "Dim";
  if (selected.length === 1) return `${selected[0].label} · ${mode}`;
  if (selected.length === 2) {
    return `${selected[0].label}, ${selected[1].label} · ${mode}`;
  }
  return `${selected.length} types · ${mode}`;
}

function withGroupRefinements(
  filter: WorkspaceFocusFilter,
  groupIds: WorkspaceFocusTypeGroupId[]
): WorkspaceFocusFilter {
  const hasReport = groupIds.includes("report");
  const hasSimulation = groupIds.includes("simulation");
  return {
    ...filter,
    groupIds,
    reportTypes: hasReport ? filter.reportTypes : [],
    simulationKind: hasSimulation ? filter.simulationKind : "any",
  };
}

function FocusTypeFilterMenu({
  filter,
  onChange,
}: {
  filter: WorkspaceFocusFilter;
  onChange: (next: WorkspaceFocusFilter) => void;
}): React.ReactElement {
  const visibilityMode = filter.visibilityMode ?? "dim";
  const selectedIds = new Set(filter.groupIds);
  const triggerLabel = focusTypeTriggerLabel(filter);

  return (
    <div data-field-select="" className="relative w-fit min-w-0 max-w-[18rem]">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            role="combobox"
            data-slot="select-trigger"
            data-size="default"
            data-reports-filter-select=""
            data-workspace-focus-type-filter=""
            className={cn(TRADE_LOG_COLUMNS_SELECT_TRIGGER_CLASS, "w-fit max-w-[18rem]")}
          >
            <span data-slot="select-value" className="truncate text-left">
              {triggerLabel}
            </span>
            <ChevronDown className="size-4 shrink-0" aria-hidden />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="start"
          className={cn("z-[200] min-w-[14rem]", TRADE_LOG_FILTER_SELECT_CONTENT_CLASS)}
          data-reports-filter-select-content=""
        >
          <DropdownMenuLabel className="text-xs font-medium opacity-70">
            Node types
          </DropdownMenuLabel>
          {WORKSPACE_FOCUS_TYPE_GROUPS.map((group) => (
            <DropdownMenuCheckboxItem
              key={group.id}
              data-workspace-focus-type-group={group.id}
              className={cn(
                TRADE_LOG_COLUMNS_MENU_CHECKBOX_ITEM_CLASS,
                TRADE_LOG_FILTER_SELECT_ITEM_CLASS
              )}
              checked={selectedIds.has(group.id)}
              onCheckedChange={() => {
                const next = toggleWorkspaceFocusGroup(filter, group.id);
                onChange(withGroupRefinements(next, next.groupIds));
              }}
              onSelect={(event) => event.preventDefault()}
            >
              {group.label}
            </DropdownMenuCheckboxItem>
          ))}

          <DropdownMenuSeparator />

          <DropdownMenuLabel className="text-xs font-medium opacity-70">
            Visibility
          </DropdownMenuLabel>
          <DropdownMenuRadioGroup
            value={visibilityMode}
            onValueChange={(value) => {
              onChange({
                ...filter,
                visibilityMode: value as WorkspaceFocusVisibilityMode,
              });
            }}
          >
            <DropdownMenuRadioItem
              value="dim"
              data-workspace-focus-visibility="dim"
              className={cn(
                TRADE_LOG_COLUMNS_MENU_CHECKBOX_ITEM_CLASS,
                TRADE_LOG_FILTER_SELECT_ITEM_CLASS
              )}
              onSelect={(event) => event.preventDefault()}
            >
              Dim the rest
            </DropdownMenuRadioItem>
            <DropdownMenuRadioItem
              value="hide"
              data-workspace-focus-visibility="hide"
              className={cn(
                TRADE_LOG_COLUMNS_MENU_CHECKBOX_ITEM_CLASS,
                TRADE_LOG_FILTER_SELECT_ITEM_CLASS
              )}
              onSelect={(event) => event.preventDefault()}
            >
              Hide the rest
            </DropdownMenuRadioItem>
          </DropdownMenuRadioGroup>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

/**
 * Canvas focus filters — multi-select types, then dim or hide the rest.
 * Same search / status chrome as Reports list filters.
 */
export function WorkspaceFocusFilterBar({
  filter,
  onChange,
  nodes,
  enrichment,
  className,
}: WorkspaceFocusFilterBarProps): React.ReactElement {
  const isBelowLg = useIsBelowLg();
  const options = React.useMemo(
    () => collectWorkspaceFocusFilterOptions(nodes, enrichment),
    [enrichment, nodes]
  );
  const filtersActive = isWorkspaceFocusFilterActive(filter);

  const selectedStatus = filter.statuses[0] ?? "";
  const selectedReportType = filter.reportTypes[0] ?? "";

  const showReportType =
    filter.groupIds.includes("report") && options.reportTypes.length > 0;
  const showSimulationFilters = filter.groupIds.includes("simulation");

  const statusOptions = React.useMemo<TradeFilterSelectOption[]>(
    () => [
      { value: ALL_VALUE, label: "All statuses" },
      ...options.statuses.map((status) => ({
        value: status,
        label: STATUS_LABELS[status] ?? status,
      })),
    ],
    [options.statuses]
  );

  const reportTypeOptions = React.useMemo<TradeFilterSelectOption[]>(
    () => [
      { value: ALL_VALUE, label: "All report types" },
      ...options.reportTypes.map((option) => ({
        value: option.value,
        label: option.label,
      })),
    ],
    [options.reportTypes]
  );

  const simulationKindOptions = React.useMemo<TradeFilterSelectOption[]>(
    () => [
      { value: "any", label: "All simulations" },
      { value: "original", label: "Originals only" },
      { value: "rerun", label: "Reruns only" },
    ],
    []
  );

  const typeFilter = <FocusTypeFilterMenu filter={filter} onChange={onChange} />;

  const secondaryFilters = (
    <>
      {options.statuses.length > 0 ? (
        <FieldSelectFit
          filterChrome="reports"
          value={toFilterValue(selectedStatus)}
          onValueChange={(value) => {
            const next = fromFilterValue(value) as WorkspaceNodeStatus | "";
            onChange({
              ...filter,
              statuses: next ? [next] : [],
            });
          }}
          options={statusOptions}
        />
      ) : null}

      {showReportType && options.reportTypes.length > 0 ? (
        <FieldSelectFit
          filterChrome="reports"
          value={toFilterValue(selectedReportType)}
          onValueChange={(value) => {
            const next = fromFilterValue(value);
            onChange({
              ...filter,
              reportTypes: next ? [next] : [],
              groupIds:
                next && filter.groupIds.length === 0 ? ["report"] : filter.groupIds,
            });
          }}
          options={reportTypeOptions}
        />
      ) : null}

      {showSimulationFilters ? (
        <FieldSelectFit
          filterChrome="reports"
          value={filter.simulationKind}
          onValueChange={(value) => {
            const next = value as WorkspaceSimulationFocusKind;
            onChange({
              ...filter,
              simulationKind: next,
              groupIds:
                next !== "any" && filter.groupIds.length === 0
                  ? ["simulation"]
                  : filter.groupIds,
            });
          }}
          options={simulationKindOptions}
        />
      ) : null}
    </>
  );

  const filterFields = (
    <>
      {typeFilter}
      {secondaryFilters}
    </>
  );

  const searchInput = (
    <Input
      value={filter.query}
      onChange={(event) => onChange({ ...filter, query: event.target.value })}
      placeholder="Search nodes…"
      data-workspace-focus-search=""
      data-reports-table-search=""
      className={cn(
        TRADE_LOG_FILTER_INPUT_CLASS,
        "h-8 normal-case tracking-normal",
        isBelowLg
          ? "min-w-0 flex-1 max-w-none"
          : "min-w-[10rem] max-w-[16rem] shrink-0"
      )}
      aria-label="Search workspace nodes"
    />
  );

  const clearButton = filtersActive ? (
    <WorkspaceDrawerCloseButton
      aria-label="Clear filters"
      data-workspace-focus-clear=""
      onClose={() => onChange(clearWorkspaceFocusFilter(filter))}
    />
  ) : null;

  const hasSecondaryFilters =
    options.statuses.length > 0 ||
    (showReportType && options.reportTypes.length > 0) ||
    showSimulationFilters;

  if (isBelowLg) {
    return (
      <div
        data-workspace-focus-filter-bar=""
        data-reports-filter-bar=""
        data-workspace-focus-filter-bar-mobile=""
        className={cn("flex min-w-0 flex-1 flex-col gap-3", className)}
      >
        <div
          data-workspace-focus-filter-bar-primary=""
          className="flex min-w-0 items-center gap-3"
        >
          {typeFilter}
          {searchInput}
          {clearButton}
        </div>
        {hasSecondaryFilters ? (
          <div
            data-workspace-focus-filter-bar-filters=""
            data-reports-filter-bar-filters=""
            className="flex min-w-0 items-center gap-3 overflow-x-auto"
          >
            {secondaryFilters}
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div
      data-workspace-focus-filter-bar=""
      data-reports-filter-bar=""
      className={cn("flex min-w-0 flex-1 flex-wrap items-center gap-3", className)}
    >
      <div
        data-workspace-focus-filter-bar-filters=""
        data-reports-filter-bar-filters=""
        className="flex min-w-0 flex-wrap items-center gap-3"
      >
        {filterFields}
      </div>

      {searchInput}

      {clearButton}
    </div>
  );
}
